import type { FrameRenderer, RenderFrameRequest, RenderedFrame } from './l3-types';
import { LabError, throwIfAborted } from './l3-errors';

type WorkerResponse =
  | { type: 'rendered'; requestId: string; bitmap: ImageBitmap }
  | { type: 'ok'; requestId: string }
  | { type: 'error'; requestId: string; message: string };

export class OffscreenCanvasRenderer implements FrameRenderer {
  private readonly worker = new Worker(new URL('./renderer-worker.ts', import.meta.url), { type: 'module' });
  private readonly pending = new Map<string, { resolve: (value: WorkerResponse) => void; reject: (reason: Error) => void; onAbort: () => void; signal: AbortSignal }>();
  private disposed = false;

  constructor() {
    this.worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => this.receive(event.data));
    this.worker.addEventListener('error', (event) => this.failAll(new LabError('RENDER_WORKER_FAILED', 'renderer-worker', event.message)));
  }

  async render(request: RenderFrameRequest, signal: AbortSignal): Promise<RenderedFrame> {
    throwIfAborted(signal, 'renderer');
    if (this.disposed) throw new LabError('RENDERER_DISPOSED', 'renderer', 'Renderer was disposed.');
    const requestId = crypto.randomUUID();
    const transfer = request.layers.map((layer) => layer.bitmap);
    const response = await new Promise<WorkerResponse>((resolve, reject) => {
      const onAbort = () => {
        this.pending.delete(requestId);
        reject(new LabError('ABORTED', 'renderer', 'Render cancelled.'));
      };
      this.pending.set(requestId, { resolve, reject, onAbort, signal });
      signal.addEventListener('abort', onAbort, { once: true });
      this.worker.postMessage({ type: 'render', requestId, request }, transfer);
    });
    if (response.type !== 'rendered') throw new LabError('RENDER_PROTOCOL_ERROR', 'renderer', 'Renderer returned an unexpected response.');
    return { bitmap: response.bitmap, width: 480, height: 480 };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.worker.terminate();
    this.failAll(new LabError('RENDERER_DISPOSED', 'renderer', 'Renderer was disposed.'));
  }

  private receive(response: WorkerResponse): void {
    const pending = this.pending.get(response.requestId);
    if (!pending) return;
    this.pending.delete(response.requestId);
    pending.signal.removeEventListener('abort', pending.onAbort);
    if (response.type === 'error') {
      const code = response.message.startsWith('GLYPH_UNSUPPORTED') ? 'GLYPH_UNSUPPORTED' : response.message;
      pending.reject(new LabError(code, 'renderer-worker', response.message));
      return;
    }
    pending.resolve(response);
  }

  private failAll(reason: Error): void {
    for (const pending of this.pending.values()) pending.reject(reason);
    this.pending.clear();
  }
}
